import { useEffect, useMemo, useState } from "react";

import { Alert } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DateTime } from "luxon";

import { useModelsIdealHookOnce } from "../../../Components/hooks/useModelsIdealHook";
import { useAuthContext } from "../../../Context/AuthContext";
import { usePriorityErpStatus } from "../../../Context/usePriorityErpStatus";
import { useUserContext } from "../../../Context/UserContext";

const useStyles = makeStyles(() => ({
  root: {
    paddingTop: 0,
    paddingBottom: 0,
  },
}));

type Props = {
  hide: boolean;
};

export const ScheduledPriorityMaintenanceAlert = ({ hide }: Props) => {
  const classes = useStyles();
  const { userRoles } = useUserContext({ requiredRoles: true });
  const { isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const [open, setOpen] = useState<boolean>(false);
  const [scheduledDownTime, setScheduledDownTime] = useState<DateTime | null>(null);
  const [enabled, setEnabled] = useState(false);
  const priorityErpStatus = usePriorityErpStatus(enabled);

  const displayToUser = useMemo(
    () => isDoitEmployee || userRoles.billingProfilesAdmin || userRoles.invoicesViewer,
    [isDoitEmployee, userRoles.billingProfilesAdmin, userRoles.invoicesViewer]
  );

  useModelsIdealHookOnce(
    "priorityErpStatus",
    4000,
    () => {
      setEnabled(true);
    },
    displayToUser
  );

  useEffect(() => {
    if (hide || !enabled) {
      return;
    }

    // Alert a week in advance
    if (priorityErpStatus.scheduledDownTime) {
      const dt = DateTime.fromJSDate(priorityErpStatus.scheduledDownTime.toDate());
      setScheduledDownTime(dt);
      const now = DateTime.now();
      const sameDay = dt.hasSame(now, "day");
      const differenceInDays = dt.diff(now, "days").days;
      if (sameDay || (differenceInDays >= 0 && differenceInDays <= 7)) {
        setOpen(true);
      }
    }
  }, [enabled, priorityErpStatus.scheduledDownTime, hide, setOpen, setScheduledDownTime, isDoitEmployee]);

  if (!open || !scheduledDownTime) {
    return null;
  }

  return (
    <Alert
      classes={{
        root: classes.root,
      }}
      onClose={() => {
        setOpen(false);
      }}
      severity="info"
      sx={{ display: "flex" }}
    >
      Scheduled maintenance on {scheduledDownTime.toFormat("MMM d")} will affect billing profiles, delay invoice
      syncing, and pause payment features
    </Alert>
  );
};
